import { ALL_COMPARATORS, type ComparatorType, CUSTOM_TIME_COMPARATORS } from 'advanced-filters/data/comparators';
import {
  DATE_INPUT_AFTER_OPTIONS,
  DATE_INPUT_BEFORE_OPTIONS,
  DATE_INPUT_OPTIONS,
} from 'advanced-filters/data/dataTypeSpecificComparatorOptions';
import { type TimeFilterOptionsType } from 'advanced-filters/store/filter';
import {
  DatePicker, Input, Select,
} from 'antd';
import dayjs from 'dayjs';
import { useCallback, useMemo } from 'react';

export default function TimeFilter({
  timeConfig,
  handleChange,
  disabled = false,
  keysMode = 'all',
  inputMode = 'all',
  allowClear = false,
} :{
  timeConfig?: TimeFilterOptionsType;
  allowClear?: boolean;
  handleChange: (args: any) => void;
  disabled?: boolean;
  keysMode?: 'before' | 'after' | 'all';
  inputMode?: 'before' | 'after' | 'all';
}) {
  const {
    filterType,
    offsetValue,
    fromTime,
    toTime,
  } = timeConfig || {};

  const handleOptionChange = useCallback((value?: ComparatorType) => {
    if (value) {
      handleChange({
        ...timeConfig,
        filterType: value,
        offsetValue: undefined,
        fromTime: undefined,
        toTime: undefined,
      });
    } else {
      handleChange(value);
    }
  }, [handleChange, timeConfig]);

  const handleCustomInput = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    handleChange({
      ...timeConfig,
      offsetValue: e.target.value,
      fromTime: undefined,
      toTime: undefined,
    });
  }, [handleChange, timeConfig]);

  const handleUpdateRange = useCallback((dates: any) => {
    if (!dates) return;

    handleChange({
      ...timeConfig,
      offsetValue: undefined,
      fromTime: dates[0].startOf('day').utc().format('YYYY-MM-DDTHH:mm:ss[Z]'),
      toTime: dates[1].endOf('day').utc().format('YYYY-MM-DDTHH:mm:ss[Z]'),
    });
  }, [handleChange, timeConfig]);

  const handleSingleDateChange = useCallback((key: 'fromTime' | 'toTime', date: any) => {
    const newTimeConfig = {
      ...timeConfig,
      fromTime: null,
      toTime: null,
      offsetValue: undefined,
      [key]: date.format('YYYY-MM-DDTHH:mm:ss[Z]'),
    };
    handleChange(newTimeConfig);
  }, [handleChange, timeConfig]);

  const customInputUi = useMemo(() => {
    if (filterType && CUSTOM_TIME_COMPARATORS.includes(filterType)) {
      if (
        filterType === ALL_COMPARATORS.last_n_hours
        || filterType === ALL_COMPARATORS.last_n_days
        || filterType === ALL_COMPARATORS.next_n_days) {
        return (
          <Input
            type="number"
            min={1}
            max={10}
            placeholder="Enter Value of N"
            value={offsetValue}
            onChange={handleCustomInput}
          />
        );
      } else {
        if (inputMode === 'before') {
          return (
            <DatePicker
              value={dayjs(fromTime)}
              onChange={(date) => handleSingleDateChange('fromTime', date)}
              minuteStep={15}
            />
          );
        } else if (inputMode === 'after') {
          return (
            <DatePicker
              value={dayjs(toTime)}
              onChange={(date) => handleSingleDateChange('toTime', date)}
              minuteStep={15}
            />
          );
        }

        return (
          <DatePicker.RangePicker
            value={[dayjs(fromTime), dayjs(toTime)]}
            onChange={handleUpdateRange}
            minuteStep={15}
          />
        );
      }
    } else {
      return null;
    }
  }, [filterType, fromTime, handleCustomInput, handleUpdateRange, handleSingleDateChange,
    inputMode, offsetValue, toTime]);

  const dateInputOptions = useMemo(() => {
    if (keysMode === 'before') {
      return DATE_INPUT_BEFORE_OPTIONS;
    } else if (keysMode === 'after') {
      return DATE_INPUT_AFTER_OPTIONS;
    }
    return DATE_INPUT_OPTIONS;
  }, [keysMode]);

  return (
    <>
      <Select
        value={filterType}
        onChange={handleOptionChange}
        options={dateInputOptions}
        placeholder="Select Time Frame"
        disabled={disabled}
        allowClear={allowClear}
      />
      {customInputUi}
    </>
  );
}
